import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { OTP_LENGTH } from '@concours/shared'

export interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  /** Appelé quand tous les chiffres sont saisis. */
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  invalid?: boolean
  label?: string
}

/**
 * Saisie d'un code à usage unique : une case par chiffre, collage du code
 * complet accepté, navigation au clavier, `autocomplete="one-time-code"`.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = OTP_LENGTH,
  disabled,
  invalid,
  label = 'Code de vérification',
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  function update(next: string) {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
    refs.current[Math.min(clean.length, length - 1)]?.focus()
  }

  function onInput(index: number, raw: string) {
    const typed = raw.replace(/\D/g, '')
    // Remplissage automatique (SMS iOS/Android, gestionnaire) : tout le code arrive d'un coup.
    if (typed.length >= length) return update(typed)
    // Frappe dans une case déjà remplie (« 3 » + « 5 ») : on garde le nouveau chiffre.
    const overwrite = typed.length === 2 && typed[0] === digits[index]
    if (typed.length > 1 && !overwrite) return update(value.slice(0, index) + typed)
    const digit = typed.slice(-1)
    if (!digit) return
    const chars = value.padEnd(length, ' ').split('')
    chars[index] = digit
    update(chars.join('').replace(/\s/g, ''))
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      event.preventDefault()
      const chars = value.split('')
      if (chars[index]) chars.splice(index, 1)
      else if (index > 0) chars.splice(index - 1, 1)
      onChange(chars.join(''))
      refs.current[Math.max(index - (value[index] ? 0 : 1), 0)]?.focus()
    } else if (event.key === 'ArrowLeft' && index > 0) refs.current[index - 1]?.focus()
    else if (event.key === 'ArrowRight' && index < length - 1) refs.current[index + 1]?.focus()
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault()
    update(event.clipboardData.getData('text'))
  }

  return (
    <fieldset className="otp" aria-invalid={invalid || undefined}>
      <legend className="field-label">{label}</legend>
      <div className="otp-boxes">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              refs.current[index] = el
            }}
            className="input otp-box"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            value={digit}
            disabled={disabled}
            aria-label={`Chiffre ${index + 1} sur ${length}`}
            aria-invalid={invalid || undefined}
            onChange={(event) => onInput(index, event.target.value)}
            onKeyDown={(event) => onKeyDown(index, event)}
            onPaste={onPaste}
            onFocus={(event) => event.target.select()}
          />
        ))}
      </div>
    </fieldset>
  )
}
